import "./styles.scss";
import React, { useState, useEffect } from "react";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useInfiniteIncidents, useIncidentById, Incident,
} from "#root/queries/incidents";
import { Button, Tooltip } from "@fluentui/react-components";
import { ROLES } from "#root/utils/roles";
import {
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    createTableColumn,
    TableColumnDefinition,
} from "@fluentui/react-table";
import { DeleteFilled, SettingsFilled, AddFilled } from "@fluentui/react-icons";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { showToast } from "#root/components/GlobalToaster";
import { ReportSettingsDialog } from "#root/components/ReportSettings";
import { CreateReportDialog } from "#root/components/CreateReportDialog";
import { useAfterActionReports, useCreateAfterActionReport, useDeleteAfterActionReport, AfterActionReport as AAReport } from "#root/queries/afterActionReport";
import { useAfterActionReportSettingsByIncidentId, useCreateAfterActionReportSettings, useUpdateAfterActionReportSettings } from "#root/queries/afterActionReportSettings";
import { DialogBox } from "#root/components/DialogBox";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useCurrentUser } from "#root/queries/local";
import { DownloadReportButton } from "#root/components/DownloadReportButton";
import { formatToLocalTime, getTimeZoneAbbreviation } from "#root/services/utils";

const formatDate = (date: string | number | Date): string => {
    let dateObj: string | Date | undefined;

    if (typeof date === "number") {
        dateObj = new Date(date);
    } else {
        dateObj = date;
    }

    return formatToLocalTime(dateObj, 'date', true);
};

export interface ReportBaseProps {
    pageTitle: string;
    settingsButtonText: string;
    settingsDialogTitle: string;
    className?: string;
}

export const ReportBase: React.FC<ReportBaseProps> = ({
    pageTitle,
    settingsButtonText,
    settingsDialogTitle,
    className = "after-action-report-page",
}) => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [openReportSettings, setOpenReportSettings] = useState(false);
    const [openCreateReportDialog, setOpenCreateReportDialog] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<AAReport | null>(null);
    const [settingsId, setSettingsId] = useState<string | null>(null);

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult,
    } = useInfiniteDropdown<Incident>({
        initialId: "",
        onChange: () => { },
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById,
    });

    const {
        data: reportsData,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useAfterActionReports({
        incidentId: selectedIncidentData?.id || "",
        searchQuery: "",
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage,
    });

    const {
        data: settingsData,
        refetch: refetchSettings,
    } = useAfterActionReportSettingsByIncidentId({
        incidentId: selectedIncidentData?.id || "",
    });

    const { data: currentUser } = useCurrentUser();

    useEffect(() => {
        if (settingsData?.item?.id) {
            setSettingsId(settingsData.item.id);
        } else {
            setSettingsId(null);
        }
    }, [settingsData]);

    const reports: AAReport[] = reportsData?.items || [];
    const totalCount = reportsData?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const createReportMutation = useCreateAfterActionReport();
    const deleteReportMutation = useDeleteAfterActionReport();

    const handleCreateReport = async (reportData: {
        reportName: string;
        startDate: Date;
        endDate: Date;
        improvementPlanTable: boolean;
    }) => {
        if (!selectedIncidentData?.id) {
            showToast("Please select an incident first", "error");
            return;
        }
        if (!currentUser) {
            showToast("User not found", "error");
            return;
        }
        try {
            await createReportMutation.mutateAsync({
                report: {
                    ...reportData,
                    incidentId: selectedIncidentData.id,
                    isSuperAdmin: currentUser.role?.toUpperCase() === ROLES.GLOBALADMIN,
                },
            });
            showToast("Report created successfully", "success");
            await refetchReports();
        } catch {
            showToast("Failed to create report", "error");
        } finally {
            setOpenCreateReportDialog(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            if (reportToDelete) {
                await deleteReportMutation.mutateAsync({ id: reportToDelete.id });
                showToast("Report deleted successfully", "success");
                await refetchReports();
            }
        } catch {
            showToast("Failed to delete report", "error");
        } finally {
            setIsDeleteDialogOpen(false);
        }
    };

    const createSettingsMutation = useCreateAfterActionReportSettings();
    const updateSettingsMutation = useUpdateAfterActionReportSettings();

    const handleSaveReportSettings = async (settingsDataForm: {
        incidentName: string;
        reportName: string;
        executiveSummary: string;
        participatingOrganizations: string;
    }) => {
        if (!selectedIncidentData?.id) {
            showToast("Please select an incident first", "error");
            return;
        }
        try {
            if (settingsId) {
                await updateSettingsMutation.mutateAsync({
                    id: settingsId,
                    settings: {
                        reportName: settingsDataForm.reportName,
                        executiveSummary: settingsDataForm.executiveSummary,
                        participatingOrganizations: settingsDataForm.participatingOrganizations,
                        incidentId: selectedIncidentData.id,
                    },
                });
            } else {
                await createSettingsMutation.mutateAsync({
                    settings: {
                        reportName: settingsDataForm.reportName,
                        executiveSummary: settingsDataForm.executiveSummary,
                        participatingOrganizations: settingsDataForm.participatingOrganizations,
                        incidentId: selectedIncidentData.id,
                    },
                });
            }
            showToast("Report settings saved", "success");
            await refetchSettings();
        } catch {
            showToast("Failed to save report settings", "error");
        }
        setOpenReportSettings(false);
    };

    const columns: TableColumnDefinition<AAReport>[] = [
        createTableColumn<AAReport>({
            columnId: "reportName",
            renderHeaderCell: () => "Report Name",
            renderCell: (item) => item.reportName,
        }),
        createTableColumn<AAReport>({
            columnId: "incident",
            renderHeaderCell: () => "Incident",
            renderCell: (item) => item.incident?.name || "",
        }),
        createTableColumn<AAReport>({
            columnId: "reportCreator",
            renderHeaderCell: () => "Report Creator",
            renderCell: (item) =>
                `${item.user?.firstName || ""} ${item.user?.lastName || ""}`,
        }),
        createTableColumn<AAReport>({
            columnId: "dateRange",
            renderHeaderCell: () => "Date Range",
            renderCell: (item) =>
                `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`,
        }),
        createTableColumn<AAReport>({
            columnId: "creationDate",
            renderHeaderCell: () => "Creation Date",
            renderCell: (item) =>
                item.createdDate ? formatDate(item.createdDate) : "",
        }),
        createTableColumn<AAReport>({
            columnId: "action",
            renderHeaderCell: () => "Action",
            renderCell: (item) => (
                <>
                    <DownloadReportButton reportId={item.id} />
                    <Button
                        icon={<DeleteFilled />}
                        appearance="subtle"
                        title="Delete Report"
                        onClick={(e) => {
                            e.stopPropagation();
                            setReportToDelete(item);
                            setIsDeleteDialogOpen(true);
                        }}
                    />
                </>
            ),
        }),
    ];

    const renderButtonWithTooltip = (
        button: React.ReactNode,
        tooltipText: string,
        disabled: boolean
    ) => {
        if (disabled) {
            return (
                <Tooltip
                    withArrow
                    content={tooltipText}
                    relationship="label"
                    positioning="below"
                >
                    <span>{button}</span>
                </Tooltip>
            );
        }
        return button;
    };

    const isIncidentNotSelected = !selectedIncidentData?.id;

    return (
        <div className={`reports-page ${className}`}>
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                    <div className="emc-page-header__props">
                        <InfiniteDropdown
                            value={selectedIncidentData?.id || ""}
                            onChange={(_, data) => handleIncidentChange(data.value)}
                            onFilterChange={setIncidentFilter}
                            queryResult={incidentQueryResult}
                            itemToOption={(incident: Incident) => ({
                                value: incident.id || "",
                                text: incident.name || "",
                            })}
                            clearable
                            placeholder="All Incidents"
                            selectedItem={selectedIncidentData}
                        />
                        {renderButtonWithTooltip(
                            <Button
                                appearance="primary"
                                icon={<SettingsFilled />}
                                onClick={() => setOpenReportSettings(true)}
                                disabled={isIncidentNotSelected}
                            >
                                {settingsButtonText}
                            </Button>,
                            "Please select an Incident first",
                            isIncidentNotSelected
                        )}
                        {renderButtonWithTooltip(
                            <Button
                                appearance="primary"
                                icon={<AddFilled />}
                                onClick={() => setOpenCreateReportDialog(true)}
                                disabled={isIncidentNotSelected}
                            >
                                Create New Report
                            </Button>,
                            "Please select an Incident first",
                            isIncidentNotSelected
                        )}
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>
                        {pageTitle}<div className="divider" />
                    </h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isReportsLoading}
                isEmpty={reports.length === 0}
                emptyMessage="No reports found."
            >
                <div className="emc-grid">
                    <DataGrid
                        className="emc-datagrid"
                        items={reports}
                        columns={columns}
                        getRowId={(item) => item.id}
                        focusMode="none"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell, columnId }) => (
                                    <DataGridHeaderCell className={String(columnId)}>
                                        {renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<AAReport>>
                            {({ item, rowId }) => (
                                <DataGridRow<AAReport> key={rowId}>
                                    {({ renderCell, columnId }) => (
                                        <DataGridCell className={String(columnId)}>
                                            {renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>

                    <Paginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={setItemsPerPage}
                        totalItems={totalCount}
                        currentItems={reports.length}
                        showItemsPerPage={true}
                    />
                </div>
            </LoadingWrapper>

            <ReportSettingsDialog
                open={openReportSettings}
                onOpenChange={(_, data) => setOpenReportSettings(data.open)}
                onSave={handleSaveReportSettings}
                title={settingsDialogTitle}
                initialData={{
                    incidentName: selectedIncidentData?.name || "",
                    reportName: settingsData?.item?.reportName || "",
                    executiveSummary: settingsData?.item?.executiveSummary || "",
                    participatingOrganizations:
                        settingsData?.item?.participatingOrganizations || "",
                }}
            />

            <CreateReportDialog
                open={openCreateReportDialog}
                onOpenChange={setOpenCreateReportDialog}
                onCreate={handleCreateReport}
                defaultReportName={settingsData?.item?.reportName || ""}
            />

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={(open) => setIsDeleteDialogOpen(open)}
                title="Delete Report"
                primaryButtonText="Delete"
                primaryButtonAppearance="primary"
                onPrimaryButtonClick={handleConfirmDelete}
                secondaryButtonText="Cancel"
                onSecondaryButtonClick={() => setIsDeleteDialogOpen(false)}
            >
                Are you sure you want to delete this report? You can't undo this.
            </DialogBox>
        </div>
    );
};

export default ReportBase;